import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  ChevronDown,
  ChevronRight,
  Database,
  Table,
  Layers,
  AlertCircle,
  CheckCircle,
  XCircle,
  BarChart3,
  TrendingUp,
  X,
  Filter,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Sector,
} from "recharts";
import axiosClientPayload from "../../../../api-client/AxiosClientPayload";
import axiosClient from "../../../../api-client/AxiosClient";

// ─── Color Palette per dimension ──────────────────────────────────────────────
const DIMENSION_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#ec4899", "#06b6d4", "#f97316",
];

const getDimColor = (index) => DIMENSION_COLORS[index % DIMENSION_COLORS.length];

// ─── Helper functions ─────────────────────────────────────────────────────────
const getQualityStatus = (percentage) => {
  if (percentage >= 90) return "Excellent";
  if (percentage >= 70) return "Needs Attention";
  return "Critical";
};

const getQualityColor = (pct) => {
  if (pct >= 90) return "text-green-600 bg-green-50 border-green-200";
  if (pct >= 70) return "text-yellow-600 bg-yellow-50 border-yellow-200";
  return "text-red-600 bg-red-50 border-red-200";
};

const getQualityIcon = (pct) => {
  if (pct >= 90) return <CheckCircle className="w-4 h-4" />;
  if (pct >= 70) return <AlertCircle className="w-4 h-4" />;
  return <XCircle className="w-4 h-4" />;
};

const getBarColor = (percentage) => {
  if (percentage >= 90) return "bg-green-500";
  if (percentage >= 70) return "bg-yellow-500";
  return "bg-red-500";
};

const getSeverityColor = (severity) => {
  if (severity === "high") return "bg-red-100 text-red-700";
  if (severity === "medium") return "bg-yellow-100 text-yellow-700";
  return "bg-blue-100 text-blue-700";
};

const getDatabaseLabelText = (selectedDatabase, loading, databases) => {
  if (selectedDatabase) return selectedDatabase;
  if (loading) return "Loading databases...";
  if (databases.length === 0) return "No databases available";
  return "Choose database";
};

const getTableLabelText = (selectedTables, loading, selectedDatabase, tables) => {
  if (selectedTables.length > 0) return `${selectedTables.length} table(s) selected`;
  if (loading) return "Loading tables...";
  if (!selectedDatabase) return "Select database first";
  if (tables.length === 0) return "No tables available";
  return "Choose tables";
};

// ─── Custom Pie active-shape ──────────────────────────────────────────────────
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  return (
    <g>
      <text x={cx} y={cy - 12} textAnchor="middle" fill={fill} style={{ fontWeight: 700, fontSize: 13 }}>
        {payload.name}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#374151" style={{ fontSize: 12 }}>
        {`${(percent * 100).toFixed(1)}%`}
      </text>
      <text x={cx} y={cy + 28} textAnchor="middle" fill="#6b7280" style={{ fontSize: 11 }}>
        {value.toFixed(2)}
      </text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 12} outerRadius={outerRadius + 16} startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  );
};

// ─── Custom Bar tooltip –──────────────────────────────────────────────────────
const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-4 min-w-[180px]">
      <p className="text-sm font-bold text-gray-800 mb-2 border-b border-gray-100 pb-2">{label}</p>
      {payload.map((entry, i) => (
        <div key={`tooltip-${i}-${entry.name}`} className="flex items-center justify-between gap-4 mt-1">
          <span className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: entry.fill }} />
            {entry.name}
          </span>
          <span className="text-xs font-semibold text-gray-800">{entry.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

CustomBarTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CustomBarTooltip.defaultProps = {
  active: false,
  payload: [],
  label: "",
};

// ─── Dimension Badge Component ────────────────────────────────────────────────
const DimensionBadge = ({ name, color, onClear }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-medium shadow-md" style={{ background: `linear-gradient(135deg, ${color}dd, ${color})` }}>
    <Filter className="w-3.5 h-3.5" />
    Filtered: {name}
    <button type="button" onClick={onClear} className="ml-1 hover:bg-white hover:bg-opacity-20 rounded-full p-0.5 transition-all" aria-label="Clear filter">
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
);

DimensionBadge.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  onClear: PropTypes.func.isRequired,
};

// ─── Filter Panel Component ───────────────────────────────────────────────────
const getFilterStatusColor = (score) => {
  if (score >= 90) return "text-green-600";
  if (score >= 70) return "text-yellow-600";
  return "text-red-600";
};

const FilterPanel = ({ activeDimension, filteredStats, clearFilter }) => {
  if (!activeDimension || !filteredStats) return null;
  const statusColor = getFilterStatusColor(filteredStats.score);

  return (
    <div className="rounded-xl p-5 flex items-center justify-between shadow-sm" style={{ background: `${activeDimension.color}0c`, border: `1px solid ${activeDimension.color}30`, borderLeft: `4px solid ${activeDimension.color}` }}>
      <div className="flex items-center gap-4 flex-wrap">
        <DimensionBadge name={activeDimension.name} color={activeDimension.color} onClear={clearFilter} />
        <div className="flex items-center gap-5 text-sm flex-wrap">
          <span className="text-gray-500">Records: <strong className="text-gray-800">{filteredStats.total?.toLocaleString()}</strong></span>
          <span className="text-green-600">Passed: <strong>{filteredStats.success?.toLocaleString()}</strong></span>
          <span className="text-red-500">Failed: <strong>{filteredStats.failed?.toLocaleString()}</strong></span>
          <span className={`font-semibold ${statusColor}`}>Score: {filteredStats.score?.toFixed(1)}%</span>
        </div>
      </div>
      <button type="button" onClick={clearFilter} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-700 transition-all" aria-label="Show all">
        <X className="w-3.5 h-3.5" /> Show All
      </button>
    </div>
  );
};

FilterPanel.propTypes = {
  activeDimension: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  }),
  filteredStats: PropTypes.shape({
    total: PropTypes.number,
    success: PropTypes.number,
    failed: PropTypes.number,
    score: PropTypes.number,
    status: PropTypes.string,
  }),
  clearFilter: PropTypes.func.isRequired,
};

FilterPanel.defaultProps = {
  activeDimension: null,
  filteredStats: null,
};

// ─── Clickable Legend Component ────────────────────────────────────────────────
const getButtonClassName = (isActive, needsOpacity) => {
  if (isActive) return "shadow-md scale-105 border-current";
  if (needsOpacity) return "opacity-35 border-gray-200 hover:opacity-60";
  return "border-transparent hover:border-gray-200 hover:bg-gray-50";
};

const ClickableLegend = ({ labels, source, handlePieClick, handleBarClick, activeDimension }) => (
  <div className="flex flex-wrap justify-center gap-2 mt-2">
    {labels.map((label, index) => {
      const isActive = activeDimension?.name === label;
      const color = getDimColor(index);
      const needsOpacity = activeDimension && !isActive;
      const buttonClass = getButtonClassName(isActive, needsOpacity);

      return (
        <button
          key={`legend-${source}-${label}`}
          type="button"
          onClick={() => source === "pie" ? handlePieClick({ name: label }, index) : handleBarClick({ activeLabel: label })}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${buttonClass}`}
          style={{ color }}
          aria-pressed={isActive}
        >
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
          {label}
        </button>
      );
    })}
  </div>
);

ClickableLegend.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  source: PropTypes.oneOf(["pie", "bar"]).isRequired,
  handlePieClick: PropTypes.func.isRequired,
  handleBarClick: PropTypes.func.isRequired,
  activeDimension: PropTypes.shape({
    name: PropTypes.string,
    color: PropTypes.string,
  }),
};

ClickableLegend.defaultProps = {
  activeDimension: null,
};

// ─── Data Quality Main Component ──────────────────────────────────────────────
const DataQuality = () => {
  const [databases, setDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState("");
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [dataSourceId, setDataSourceId] = useState(null);
  const [loading, setLoading] = useState({ databases: false, tables: false, analysis: false });
  const [error, setError] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [expandedDimensions, setExpandedDimensions] = useState({});
  const [expandedTables, setExpandedTables] = useState({});
  const [showDatabaseDropdown, setShowDatabaseDropdown] = useState(false);
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [chartData, setChartData] = useState({ pieChart: null, barChart: null });
  const [activeDimension, setActiveDimension] = useState(null);
  const [activePieIndex, setActivePieIndex] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_DQ_BASE_URL;

  const getCredentials = () => ({
    token: localStorage.getItem("token") || "",
    organization_config_id: localStorage.getItem("organization_config_id") || "",
    data_source_id: dataSourceId || "",
  });

  const fetchDataSourceId = async (configId) => {
    try {
      const response = await axiosClient.get(`api/MappingDB/CorpMappingDBByUserCorp?OrganizationConfigId=${configId}`);
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setDataSourceId(data[0].dataSourceId);
        return data[0].dataSourceId;
      }
      setError("No data source found for the selected configuration");
      return null;
    } catch (err) {
      setError("Failed to fetch data source configuration");
      console.error("Error fetching data source:", err);
      return null;
    }
  };

  useEffect(() => {
    const configId = localStorage.getItem("organization_config_id");
    if (configId) fetchDataSourceId(configId);
  }, []);

  useEffect(() => {
    if (dataSourceId) fetchDatabases();
  }, [dataSourceId]);

  useEffect(() => {
    if (selectedDatabase) fetchTables();
    else {
      setTables([]);
      setSelectedTables([]);
    }
  }, [selectedDatabase]);

  const fetchDatabases = async () => {
    if (!dataSourceId) {
      setError("Data source ID not available.");
      return;
    }
    setLoading((p) => ({ ...p, databases: true }));
    setError(null);
    try {
      const c = getCredentials();
      const response = await axiosClientPayload.post(`${API_BASE_URL}/databases`, {
        token: c.token,
        organization_config_id: c.organization_config_id,
        data_source_id: c.data_source_id,
      });
      setDatabases(response.data?.data?.databases || []);
    } catch (err) {
      setError("Failed to fetch databases.");
      console.error("Error fetching databases:", err);
      setDatabases([]);
    } finally {
      setLoading((p) => ({ ...p, databases: false }));
    }
  };

  const fetchTables = async () => {
    if (!dataSourceId) {
      setError("Data source ID not available.");
      return;
    }
    setLoading((p) => ({ ...p, tables: true }));
    setError(null);
    try {
      const c = getCredentials();
      const response = await axiosClientPayload.post(`${API_BASE_URL}/tables`, {
        token: c.token,
        organization_config_id: c.organization_config_id,
        data_source_id: c.data_source_id,
        database: selectedDatabase,
      });
      setTables(response.data?.data?.tables || []);
    } catch (err) {
      setError("Failed to fetch tables.");
      console.error("Error fetching tables:", err);
      setTables([]);
    } finally {
      setLoading((p) => ({ ...p, tables: false }));
    }
  };

  const handleAnalyze = async () => {
    if (!selectedDatabase || selectedTables.length === 0) {
      setError("Please select database and at least one table");
      return;
    }
    if (!dataSourceId) {
      setError("Data source ID not available.");
      return;
    }
    setLoading((p) => ({ ...p, analysis: true }));
    setError(null);
    setActiveDimension(null);
    setActivePieIndex(null);
    try {
      const c = getCredentials();
      const response = await axiosClientPayload.post(`${API_BASE_URL}/analyze`, {
        token: c.token,
        organization_config_id: c.organization_config_id,
        data_source_id: c.data_source_id,
        database: selectedDatabase,
        table_names: selectedTables,
      });
      setAnalysisData(response.data);
      setChartData({ pieChart: response.data?.data?.dimension_pie_chart || null, barChart: response.data?.data?.success_failed_bar_chart || null });
    } catch (err) {
      setError("Failed to analyze data.");
      console.error("Error analyzing data:", err);
    } finally {
      setLoading((p) => ({ ...p, analysis: false }));
    }
  };

  const handlePieClick = useCallback((data, index) => {
    const dimName = data?.name;
    if (!dimName) return;
    if (activeDimension?.name === dimName) {
      setActiveDimension(null);
      setActivePieIndex(null);
    } else {
      setActiveDimension({ name: dimName, color: getDimColor(index) });
      setActivePieIndex(index);
      setExpandedDimensions((prev) => {
        const updates = {};
        analysisData?.data?.dimension_analysis?.forEach((d) => {
          if (d.dimension.toLowerCase() === dimName.toLowerCase()) {
            updates[d.dimension] = true;
          }
        });
        return { ...prev, ...updates };
      });
    }
  }, [activeDimension, analysisData]);

  const handleBarClick = useCallback((data) => {
    if (!data?.activeLabel) return;
    const dimName = data.activeLabel;
    const dimIndex = chartData?.barChart?.success?.labels?.indexOf(dimName) ?? 0;
    if (activeDimension?.name === dimName) {
      setActiveDimension(null);
      setActivePieIndex(null);
    } else {
      setActiveDimension({ name: dimName, color: getDimColor(dimIndex) });
      setActivePieIndex(null);
      setExpandedDimensions((prev) => {
        const updates = {};
        analysisData?.data?.dimension_analysis?.forEach((d) => {
          if (d.dimension.toLowerCase() === dimName.toLowerCase()) {
            updates[d.dimension] = true;
          }
        });
        return { ...prev, ...updates };
      });
    }
  }, [activeDimension, chartData, analysisData]);

  const clearFilter = () => {
    setActiveDimension(null);
    setActivePieIndex(null);
  };

  const filteredDimensions = analysisData?.data?.dimension_analysis?.filter((d) => !activeDimension || d.dimension.toLowerCase() === activeDimension.name.toLowerCase()) || [];

  const getFilteredStats = () => {
    if (!activeDimension) return null;
    const dim = analysisData?.data?.dimension_analysis?.find((d) => d.dimension.toLowerCase() === activeDimension.name.toLowerCase());
    if (!dim) return null;
    return {
      total: dim.total_records,
      failed: dim.failed_records,
      success: (dim.total_records || 0) - (dim.failed_records || 0),
      score: dim.percentage,
      status: getQualityStatus(dim.percentage),
    };
  };

  const filteredStats = getFilteredStats();

  const toggleDimension = (dimension) => setExpandedDimensions((p) => ({ ...p, [dimension]: !p[dimension] }));
  const toggleTable = (dk, tn) => {
    const k = `${dk}-${tn}`;
    setExpandedTables((p) => ({ ...p, [k]: !p[k] }));
  };

  const parseAIAnalysis = (text) => {
    if (!text) return [];
    return text.split(/\n\n(?=\d+\.)/).map((point, index) => {
      const numberMatch = point.match(/^(\d+)\./);
      const number = numberMatch ? numberMatch[1] : String(index + 1);
      let content = point.replace(/^\d+\.\s*/, "").trim();
      let title = "";
      const tm = content.match(/^([^:]+?):\s*(.+)$/s);
      if (tm) {
        title = tm[1].trim();
        content = tm[2].trim();
      } else {
        const sm = content.match(/^([^.]{1,100})\.\s*(.+)$/s);
        if (sm) {
          title = sm[1].trim();
          content = sm[2].trim();
        }
      }
      return { number, title, content: content || point.replace(/^\d+\.\s*/, "").trim(), fullText: point.replace(/^\d+\.\s*/, "").trim() };
    });
  };

  useEffect(() => {
    const handle = (e) => {
      if (!e.target.closest(".dropdown-container")) {
        setShowDatabaseDropdown(false);
        setShowTableDropdown(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const cards = [
    {
      id: "total-records",
      label: "Total Records",
      icon: <Database className="w-5 h-5 text-blue-600" />,
      value: (activeDimension ? filteredStats?.total : analysisData?.data?.total_records_analyzed)?.toLocaleString() || "N/A",
      sub: activeDimension ? `Dimension: ${activeDimension.name}` : `${analysisData?.data?.total_tables_analyzed || 0} tables analyzed`,
      g: "from-blue-50 to-blue-100",
      b: "border-blue-200",
      lc: "text-blue-700",
      vc: "text-blue-900",
      sc: "text-blue-600",
    },
    {
      id: "overall-quality",
      label: "Overall Quality",
      icon: <TrendingUp className="w-5 h-5 text-green-600" />,
      value: `${(activeDimension ? filteredStats?.score : analysisData?.data?.overall_score)?.toFixed(1) || "N/A"}%`,
      sub: activeDimension ? filteredStats?.status : analysisData?.data?.overall_status || "N/A",
      g: "from-green-50 to-green-100",
      b: "border-green-200",
      lc: "text-green-700",
      vc: "text-green-900",
      sc: "text-green-600",
    },
    {
      id: "success-records",
      label: "Success Records",
      icon: <CheckCircle className="w-5 h-5 text-emerald-600" />,
      value: (activeDimension ? filteredStats?.success : analysisData?.data?.total_success_records)?.toLocaleString() || "N/A",
      sub: "Records passed",
      g: "from-emerald-50 to-emerald-100",
      b: "border-emerald-200",
      lc: "text-emerald-700",
      vc: "text-emerald-900",
      sc: "text-emerald-600",
    },
    {
      id: "failed-records",
      label: "Failed Records",
      icon: <XCircle className="w-5 h-5 text-red-600" />,
      value: (activeDimension ? filteredStats?.failed : analysisData?.data?.total_failed_records)?.toLocaleString() || "N/A",
      sub: "Issues detected",
      g: "from-red-50 to-red-100",
      b: "border-red-200",
      lc: "text-red-700",
      vc: "text-red-900",
      sc: "text-red-600",
    },
  ];

  return (
    <div className="w-full space-y-6 p-6">
      {/* ── Selection Panel ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-600" /> Data Quality Analysis
        </h2>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-end">
          {/* Database dropdown */}
          <div className="relative dropdown-container flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="database-select">
              Database <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              id="database-select"
              onClick={() => setShowDatabaseDropdown(!showDatabaseDropdown)}
              disabled={loading.databases || databases.length === 0}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-left flex items-center justify-between"
            >
              <span className="text-sm text-gray-700">{getDatabaseLabelText(selectedDatabase, loading.databases, databases)}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showDatabaseDropdown ? "rotate-180" : ""}`} />
            </button>
            {showDatabaseDropdown && databases.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto top-full">
                {databases.map((db) => (
                  <button
                    type="button"
                    key={db}
                    onClick={() => {
                      setSelectedDatabase(db);
                      setShowDatabaseDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-blue-50 text-sm text-gray-700 transition-colors"
                  >
                    {db}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tables dropdown */}
          <div className="relative dropdown-container flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="tables-select">
              Tables <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              id="tables-select"
              onClick={() => setShowTableDropdown(!showTableDropdown)}
              disabled={!selectedDatabase || loading.tables || tables.length === 0}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-left flex items-center justify-between"
            >
              <span className="text-sm text-gray-700">{getTableLabelText(selectedTables, loading.tables, selectedDatabase, tables)}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showTableDropdown ? "rotate-180" : ""}`} />
            </button>
            {showTableDropdown && tables.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto top-full">
                <label className="flex items-center px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-200 bg-gray-50">
                  <input
                    type="checkbox"
                    checked={tables.length > 0 && tables.every((t) => selectedTables.includes(t.table_name))}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedTables(tables.map((t) => t.table_name));
                      else setSelectedTables([]);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    aria-label="Select all tables"
                  />
                  <span className="ml-3 text-sm font-semibold text-gray-700">Select All</span>
                </label>
                {tables.map((table) => (
                  <label key={`table-${table.table_name}`} className="flex items-center px-4 py-2 hover:bg-blue-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedTables.includes(table.table_name)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedTables([...selectedTables, table.table_name]);
                        else setSelectedTables(selectedTables.filter((t) => t !== table.table_name));
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      aria-label={`Select table ${table.table_name}`}
                    />
                    <span className="ml-3 text-sm text-gray-700">{table.table_name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Analyze button */}
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-transparent mb-2" htmlFor="analyze-btn">
              Action
            </label>
            <button
              type="button"
              id="analyze-btn"
              onClick={handleAnalyze}
              disabled={!selectedDatabase || selectedTables.length === 0 || loading.analysis}
              className="w-full px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {loading.analysis ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4" />
                  Analyze
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Analysis Results ───────────────────────────────────────────────── */}
      {analysisData && (
        <div className="space-y-6">
          {/* Header + AI Insights */}
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Analysis Results</h2>
            {analysisData.data?.ai_analysis && (
              <button
                type="button"
                onClick={() => setShowAIInsights(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI Insights
              </button>
            )}
          </div>

          {/* Active filter banner */}
          <FilterPanel activeDimension={activeDimension} filteredStats={filteredStats} clearFilter={clearFilter} />

          {/* Overview cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map((c) => (
              <div key={c.id} className={`bg-gradient-to-br ${c.g} rounded-xl shadow-sm border ${c.b} p-6 transition-all duration-300`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-semibold ${c.lc}`}>{c.label}</span>
                  {c.icon}
                </div>
                <p className={`text-3xl font-bold ${c.vc}`}>{c.value}</p>
                <p className={`text-sm ${c.sc} mt-2`}>{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Charts */}
          {(chartData.pieChart || chartData.barChart) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              {chartData.pieChart && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-600" /> Quality Dimension Distribution
                    </h3>
                    {activeDimension && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium whitespace-nowrap">Click again to reset</span>}
                  </div>
                  <p className="text-xs text-gray-400 mb-3">Click a slice or legend item to filter the entire dashboard</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData.pieChart.labels.map((label, i) => ({ name: label, value: chartData.pieChart.values[i] }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        dataKey="value"
                        activeIndex={activePieIndex}
                        activeShape={renderActiveShape}
                        onClick={(data, index) => handlePieClick(data, index)}
                        style={{ cursor: "pointer" }}
                      >
                        {chartData.pieChart.labels.map((entry, index) => (
                          <Cell
                            key={`cell-${entry}`}
                            fill={getDimColor(index)}
                            opacity={activeDimension && activeDimension.name !== entry ? 0.2 : 1}
                            style={{ transition: "opacity 0.25s ease" }}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                      <Legend
                        content={(
                          <ClickableLegend
                            labels={chartData.pieChart?.labels || []}
                            source="pie"
                            handlePieClick={handlePieClick}
                            handleBarClick={() => {}}
                            activeDimension={activeDimension}
                          />
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Bar Chart */}
              {chartData.barChart && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" /> Success vs Failed Records
                    </h3>
                    {activeDimension && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium whitespace-nowrap">Click again to reset</span>}
                  </div>
                  <p className="text-xs text-gray-400 mb-3">Click a bar to filter the dashboard by that dimension</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={chartData.barChart.success.labels.map((label, index) => ({
                        name: label,
                        success: chartData.barChart.success.values[index],
                        failed: chartData.barChart.failed.values[index],
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      onClick={handleBarClick}
                      style={{ cursor: "pointer" }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Legend />
                      <Bar dataKey="success" stackId="a" name="Success Records">
                        {chartData.barChart.success.labels.map((label, index) => (
                          <Cell
                            key={`s-${label}`}
                            fill="#10b981"
                            opacity={!activeDimension || activeDimension.name === label ? 1 : 0.18}
                            style={{ transition: "opacity 0.25s ease" }}
                          />
                        ))}
                      </Bar>
                      <Bar dataKey="failed" stackId="a" name="Failed Records" radius={[4, 4, 0, 0]}>
                        {chartData.barChart.success.labels.map((label, index) => (
                          <Cell
                            key={`f-${label}`}
                            fill="#ef4444"
                            opacity={!activeDimension || activeDimension.name === label ? 1 : 0.18}
                            style={{ transition: "opacity 0.25s ease" }}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* ── Hierarchical Table ────────────────────────────────────── */}
          <HierarchicalTable filteredDimensions={filteredDimensions} analysisData={analysisData} activeDimension={activeDimension} clearFilter={clearFilter} expandedDimensions={expandedDimensions} expandedTables={expandedTables} toggleDimension={toggleDimension} toggleTable={toggleTable} />
        </div>
      )}

      {/* ── AI Insights Modal ──────────────────────────────────────────── */}
      <AIInsightsModal showAIInsights={showAIInsights} setShowAIInsights={setShowAIInsights} analysisData={analysisData} parseAIAnalysis={parseAIAnalysis} />
    </div>
  );
};

// ─── Hierarchical Table Component ──────────────────────────────────────────────
const HierarchicalTable = ({ filteredDimensions, analysisData, activeDimension, clearFilter, expandedDimensions, expandedTables, toggleDimension, toggleTable }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Layers className="w-5 h-5 text-blue-600" /> Table Health Overview — Hierarchical View
        </h3>
        {activeDimension && (
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: activeDimension.color }} />
            <span className="text-sm font-medium text-gray-600">
              Showing: <strong style={{ color: activeDimension.color }}>{activeDimension.name}</strong>
            </span>
            <button type="button" onClick={clearFilter} className="ml-2 text-xs text-gray-400 hover:text-gray-600 underline transition-colors" aria-label="Clear filter">
              Clear filter
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {["Dimension / Table / Column", "Total Records", "Bad Records", "Quality %", "Rule Used"].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDimensions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-gray-400 text-sm">
                  No dimension data available
                </td>
              </tr>
            ) : (
              filteredDimensions.map((dimension, dimIdx) => (
                <DimensionRow
                  key={`dim-${dimension.dimension}`}
                  dimension={dimension}
                  dimIdx={dimIdx}
                  analysisData={analysisData}
                  activeDimension={activeDimension}
                  expandedDimensions={expandedDimensions}
                  expandedTables={expandedTables}
                  toggleDimension={toggleDimension}
                  toggleTable={toggleTable}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

HierarchicalTable.propTypes = {
  filteredDimensions: PropTypes.arrayOf(PropTypes.object),
  analysisData: PropTypes.object,
  activeDimension: PropTypes.object,
  clearFilter: PropTypes.func,
  expandedDimensions: PropTypes.object,
  expandedTables: PropTypes.object,
  toggleDimension: PropTypes.func,
  toggleTable: PropTypes.func,
};

HierarchicalTable.defaultProps = {
  filteredDimensions: [],
  analysisData: null,
  activeDimension: null,
  clearFilter: () => {},
  expandedDimensions: {},
  expandedTables: {},
  toggleDimension: () => {},
  toggleTable: () => {},
};

// ─── Dimension Row Component ──────────────────────────────────────────────────
const DimensionRow = ({ dimension, dimIdx, analysisData, activeDimension, expandedDimensions, expandedTables, toggleDimension, toggleTable }) => {
  const globalIdx = analysisData.data?.dimension_analysis?.findIndex((d) => d.dimension === dimension.dimension) ?? dimIdx;
  const dimColor = getDimColor(globalIdx);

  return (
    <React.Fragment key={`dim-frag-${dimension.dimension}`}>
      {/* Dimension row */}
      <tr className="hover:brightness-95 transition-all" style={{ background: `${dimColor}12` }}>
        <td className="px-6 py-4">
          <button
            type="button"
            onClick={() => toggleDimension(dimension.dimension)}
            className="flex items-center gap-2 text-sm font-bold hover:opacity-75 transition-opacity"
            style={{ color: dimColor }}
          >
            {expandedDimensions[dimension.dimension] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Layers className="w-4 h-4" />
            {dimension.dimension}
          </button>
        </td>
        <td className="px-6 py-4 text-sm font-medium text-gray-900">{dimension.total_records?.toLocaleString() || "-"}</td>
        <td className="px-6 py-4 text-sm font-medium text-red-600">{dimension.failed_records?.toLocaleString() || "-"}</td>
        <td className="px-6 py-4">
          <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border ${getQualityColor(dimension.percentage)}`}>
            {getQualityIcon(dimension.percentage)}
            <span className="text-sm font-semibold">{dimension.percentage?.toFixed(1) || "0"}%</span>
          </div>
        </td>
        <td className="px-6 py-4 text-sm text-gray-400">—</td>
      </tr>

      {/* Table rows */}
      {expandedDimensions[dimension.dimension] &&
        analysisData.data?.table_analysis?.filter((t) => t.dimension.toLowerCase() === dimension.dimension.toLowerCase()).map((table) => (
          <TableRow
            key={`table-${dimension.dimension}-${table.table_name}`}
            table={table}
            dimension={dimension}
            analysisData={analysisData}
            expandedTables={expandedTables}
            toggleTable={toggleTable}
          />
        ))}
    </React.Fragment>
  );
};

DimensionRow.propTypes = {
  dimension: PropTypes.object.isRequired,
  dimIdx: PropTypes.number.isRequired,
  analysisData: PropTypes.object,
  activeDimension: PropTypes.object,
  expandedDimensions: PropTypes.object.isRequired,
  expandedTables: PropTypes.object.isRequired,
  toggleDimension: PropTypes.func.isRequired,
  toggleTable: PropTypes.func.isRequired,
};

// ─── Table Row Component ───────────────────────────────────────────────────────
const TableRow = ({ table, dimension, analysisData, expandedTables, toggleTable }) => {
  return (
    <React.Fragment key={`table-frag-${dimension.dimension}-${table.table_name}`}>
      <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
        <td className="px-6 py-3">
          <button
            type="button"
            onClick={() => toggleTable(dimension.dimension, table.table_name)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-800 hover:text-gray-600 ml-8"
          >
            {expandedTables[`${dimension.dimension}-${table.table_name}`] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Table className="w-4 h-4" /> {table.table_name}
          </button>
        </td>
        <td className="px-6 py-3 text-sm text-gray-700">{table.total_records?.toLocaleString() || "-"}</td>
        <td className="px-6 py-3 text-sm text-red-600">{table.failed_records?.toLocaleString() || "-"}</td>
        <td className="px-6 py-3">
          <div className="flex items-center gap-2">
            <div className="w-full bg-gray-200 rounded-full h-2 max-w-[120px]">
              <div className={`h-2 rounded-full ${getBarColor(table.percentage || 0)}`} style={{ width: `${table.percentage || 0}%` }} />
            </div>
            <span className="text-sm font-medium text-gray-700">{table.percentage?.toFixed(1) || "0"}%</span>
          </div>
        </td>
        <td className="px-6 py-3 text-sm text-gray-400">—</td>
      </tr>

      {/* Column rows */}
      {expandedTables[`${dimension.dimension}-${table.table_name}`] && (
        <ColumnRows table={table} dimension={dimension} analysisData={analysisData} />
      )}
    </React.Fragment>
  );
};

TableRow.propTypes = {
  table: PropTypes.object.isRequired,
  dimension: PropTypes.object.isRequired,
  analysisData: PropTypes.object,
  expandedTables: PropTypes.object.isRequired,
  toggleTable: PropTypes.func.isRequired,
};

// ─── Column Rows Component ─────────────────────────────────────────────────────
const ColumnRows = ({ table, dimension, analysisData }) => {
  const cols = analysisData.data?.column_analysis?.filter((col) => col.table_name === table.table_name && col.dimension.toLowerCase() === dimension.dimension.toLowerCase()) || [];
  const dimColor = getDimColor(analysisData.data?.dimension_analysis?.findIndex((d) => d.dimension === dimension.dimension) ?? 0);

  if (cols.length === 0) {
    return (
      <tr key={`no-cols-${table.table_name}`}>
        <td colSpan={5} className="px-6 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-400 italic ml-16">
            <div className="w-2 h-2 bg-gray-300 rounded-full" />
            No column-level data available for this dimension
          </div>
        </td>
      </tr>
    );
  }

  return cols.map((col) => (
    <tr key={`col-${col.column_name}`} className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-2">
        <div className="flex items-center gap-2 text-sm text-gray-600 ml-16">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dimColor }} />
          {col.column_name}
        </div>
      </td>
      <td className="px-6 py-2 text-sm text-gray-600">{col.total_records?.toLocaleString() || "-"}</td>
      <td className="px-6 py-2 text-sm text-red-500">{col.failed_records?.toLocaleString() || "-"}</td>
      <td className="px-6 py-2">
        <div className="flex items-center gap-2">
          <ColumnPercentageBadge percentage={col.percentage} />
          {col.severity && <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(col.severity)}`}>{col.severity}</span>}
        </div>
      </td>
      <td className="px-6 py-2">
        {col.rule_used ? (
          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded max-w-xs block truncate" title={col.rule_used}>
            {col.rule_used}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
    </tr>
  ));
};

ColumnRows.propTypes = {
  table: PropTypes.object.isRequired,
  dimension: PropTypes.object.isRequired,
  analysisData: PropTypes.object,
};

// ─── Column Percentage Badge Component ────────────────────────────────────────
const getColumnPercentageClass = (percentage) => {
  if (percentage >= 90) return "bg-green-100 text-green-700";
  if (percentage >= 70) return "bg-yellow-100 text-yellow-700";
  return "bg-red-100 text-red-700";
};

const ColumnPercentageBadge = ({ percentage }) => (
  <span className={`text-xs font-medium px-2 py-1 rounded ${getColumnPercentageClass(percentage)}`}>
    {percentage?.toFixed(1) || "0"}%
  </span>
);

ColumnPercentageBadge.propTypes = {
  percentage: PropTypes.number,
};

ColumnPercentageBadge.defaultProps = {
  percentage: 0,
};

// ─── AI Insights Modal Component ──────────────────────────────────────────────
const AIInsightsModal = ({ showAIInsights, setShowAIInsights, analysisData, parseAIAnalysis }) => {
  if (!showAIInsights || !analysisData?.data?.ai_analysis) return null;

  const handleClose = () => setShowAIInsights(false);
  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleClose();
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        onClick={handleClose}
        className="absolute inset-0 bg-black bg-opacity-50"
        aria-label="Close modal"
        onKeyDown={handleKeyDown}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative z-10">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h3 className="text-2xl font-bold">AI-Powered Insights</h3>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-purple-100 mt-2">Automated analysis and recommendations for your data quality</p>
          </div>
          <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 140px)" }}>
            <AIInsightsContent analysisData={analysisData} parseAIAnalysis={parseAIAnalysis} />
          </div>
        </div>
      </div>
    </div>
  );
};

AIInsightsModal.propTypes = {
  showAIInsights: PropTypes.bool.isRequired,
  setShowAIInsights: PropTypes.func.isRequired,
  analysisData: PropTypes.object,
  parseAIAnalysis: PropTypes.func.isRequired,
};

// ─── AI Insights Content Component ─────────────────────────────────────────────
const AIInsightsContent = ({ analysisData, parseAIAnalysis }) => {
  const parsedInsights = parseAIAnalysis(analysisData.data.ai_analysis);

  if (parsedInsights.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <p className="text-gray-500">No AI insights available for this analysis</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {parsedInsights.map((insight) => (
        <InsightItem key={`insight-${insight.number}`} insight={insight} />
      ))}
    </div>
  );
};

AIInsightsContent.propTypes = {
  analysisData: PropTypes.object.isRequired,
  parseAIAnalysis: PropTypes.func.isRequired,
};

// ─── Insight Item Component ────────────────────────────────────────────────────
const InsightItem = ({ insight }) => {
  const hasActions = insight.content.includes("recommended actions") || insight.content.includes("Top 3");
  const showActions = hasActions;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border-l-4 border-purple-500 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
          {insight.number}
        </div>
        <div className="flex-1">
          {insight.title && <h4 className="font-bold text-gray-900 text-lg mb-3">{insight.title}</h4>}
          {showActions ? (
            <>
              <div className="text-gray-700 leading-relaxed mb-4">
                {insight.content.split(/\n/).filter((l) => !l.trim().startsWith("-") && !l.trim().match(/^\d+\./) && l.trim().length > 0).join(" ")}
              </div>
              <div className="mt-4 space-y-3">
                {insight.content
                  .split(/\n/)
                  .filter((l) => l.trim().startsWith("-") || l.trim().startsWith("•") || (l.trim().match(/^\d+\./) && l.trim().length > 3))
                  .map((action, ai) => (
                    <div key={`action-${insight.number}-${ai}`} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-purple-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-gray-700 flex-1 leading-relaxed">{action.trim().replace(/^[-•]\s*/, "").replace(/^\d+\.\s*/, "")}</span>
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <div className="text-gray-700 leading-relaxed whitespace-pre-line">{insight.content || insight.fullText}</div>
          )}
        </div>
      </div>
    </div>
  );
};

InsightItem.propTypes = {
  insight: PropTypes.shape({
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    content: PropTypes.string.isRequired,
    fullText: PropTypes.string,
  }).isRequired,
};

export default DataQuality;
